import { SolShortcut } from '../../services/shortcut.service';
import { ObjectTools } from '../../tools/object-tools';
import { SolText } from './sol-text';

export type SolDialogButtonPosition = 'left' | 'right';

export type SolDialogType = 'default' | 'danger' | 'chromeless';

export type SolDialogSize = 'default' | 'small' | 'medium' | 'large';

export type SolDialogWidth = 'default' | 'small' | 'medium' | 'large';

export type SolDialogHeight = 'default' | 'small' | 'medium' | 'large';

export class SolDialogButton {
  constructor(
    public readonly id: string,
    public readonly text: SolText,
    public readonly shortcut: SolShortcut | undefined,
    public readonly position: SolDialogButtonPosition,
  ) {}

  public static of(
    id: string,
    text: SolText,
    shortcut?: SolShortcut,
    position: SolDialogButtonPosition = 'right',
  ): SolDialogButton {
    return new SolDialogButton(id, text, shortcut, position);
  }

  public static ofClose(): SolDialogButton {
    return SolDialogButton.of('close', SolText.of('sol-common.close'), SolShortcut.of('Escape'));
  }

  public static ofCancel(): SolDialogButton {
    return SolDialogButton.of('cancel', SolText.of('sol-common.cancel'), SolShortcut.of('Escape'));
  }

  public static ofConfirm(confirmText?: SolText): SolDialogButton {
    confirmText = confirmText !== undefined ? confirmText : SolText.of('sol-common.confirm');
    return SolDialogButton.of('confirm', confirmText, SolShortcut.of('Ctrl+Enter'));
  }

  public withText(text: SolText): SolDialogButton {
    return this.copy({ text });
  }

  public withShortcut(shortcut: SolShortcut | undefined): SolDialogButton {
    return this.copy({ shortcut });
  }

  public withPosition(position: SolDialogButtonPosition): SolDialogButton {
    return this.copy({ position });
  }

  private copy(props: Partial<SolDialogButton>): SolDialogButton {
    return ObjectTools.copy<SolDialogButton>(this, props);
  }
}

export class SolDialog {
  public static readonly button = SolDialogButton;

  constructor(
    public readonly title: SolText,
    public readonly buttons: SolDialogButton[],
    public readonly escapeButtonId: string = '',
    public readonly primaryButtonId: string = '',
    public readonly type: SolDialogType = 'default',
    public readonly size: SolDialogSize = 'default',
    public readonly loadingActionId: string | undefined = undefined,
    public readonly width: SolDialogWidth = 'default',
    public readonly height: SolDialogHeight = 'default',
  ) {}

  // ─── Factories ───

  public static of(title: SolText): SolDialog {
    return new SolDialog(title, []);
  }

  public static ofInfo(title: SolText): SolDialog {
    return new SolDialog(title, [SolDialogButton.ofClose()]).withPrimaryButtonId('close').withEscapeButtonId('close');
  }

  public static ofConfirm(title: SolText, confirmText?: SolText): SolDialog {
    return new SolDialog(title, [
      SolDialogButton.ofCancel().withPosition('left'),
      SolDialogButton.ofConfirm(confirmText),
    ])
      .withPrimaryButtonId('confirm')
      .withEscapeButtonId('cancel');
  }

  public static ofChromeless(): SolDialog {
    return new SolDialog(SolText.of(''), []).withType('chromeless');
  }

  // ─── Loading ───

  public get loading(): boolean {
    return this.loadingActionId !== undefined;
  }

  public startLoading(actionId: string = 'confirm'): SolDialog {
    return this.copy({ loadingActionId: actionId });
  }

  public stopLoading(): SolDialog {
    return this.copy({ loadingActionId: undefined });
  }

  // ─── Configuration ───

  public withType(type: SolDialogType): SolDialog {
    return this.copy({ type });
  }
  public withSize(size: SolDialogSize): SolDialog {
    return this.copy({
      size,
      width: size === 'default' ? 'default' : size,
      height: size === 'default' ? 'default' : size,
    });
  }
  public withWidth(width: SolDialogWidth): SolDialog {
    return this.copy({ width });
  }
  public withHeight(height: SolDialogHeight): SolDialog {
    return this.copy({ height });
  }
  public withButton(actionId: string, mapper: (b: SolDialogButton) => SolDialogButton): SolDialog {
    return this.copy({ buttons: this.buttons.map(b => (b.id === actionId ? mapper(b) : b)) });
  }
  public withButtons(buttons: SolDialogButton[]): SolDialog {
    return this.copy({ buttons });
  }
  public withEscapeButtonId(id: string): SolDialog {
    return this.copy({ escapeButtonId: id });
  }
  public withPrimaryButtonId(id: string): SolDialog {
    return this.copy({ primaryButtonId: id });
  }

  private copy(props: Partial<SolDialog>): SolDialog {
    return ObjectTools.copy<SolDialog>(this, props);
  }
}
